import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import jwtDecode from 'jwt-decode';
import CircularProgress from '@mui/material/CircularProgress';
import { Box } from '@mui/material';
import useAuth from '../hooks/useAuth';
import { axiosIntegrationInstance } from '../services/axios';
import { useToast } from '../components/useToast';
import { convertAxiosErrorToUserFacingMessage } from '../context/ApiClientContext';
import useSiteSettings from '../hooks/useSiteSettings';

const VALID_STATES = {
  POINT_CLICK_CARE: 'pointClickCare',
  ATHENA: 'athena',
};

const handlePointClickCareAuth = (response, {
  setAuth,
  setPersist,
  setPointClickCareAuth,
  updateSiteSettings,
}) => {
  const {
    accessToken,
    pointClickCare,
  } = response.data;
  const decoded = jwtDecode(accessToken);
  setAuth({ accessToken });
  setPersist(true);
  setPointClickCareAuth(pointClickCare);
  updateSiteSettings(decoded.siteIds);
};

const handleAthenaAuth = (response, {
  setAuth,
  setPersist,
  setMKey,
  setActiveSiteId,
}) => {
  const {
    accessToken,
    mkey,
  } = response.data;
  const decoded = jwtDecode(accessToken);
  setAuth({ accessToken });
  setMKey(mkey);
  setPersist(true);
  setActiveSiteId(decoded.siteIds[0]);
};

const AUTH_HANDLERS = {
  [VALID_STATES.POINT_CLICK_CARE]: handlePointClickCareAuth,
  [VALID_STATES.ATHENA]: handleAthenaAuth,
};

const NAVIGATION_HANDLERS = {
  [VALID_STATES.POINT_CLICK_CARE]: () => '/home',
  [VALID_STATES.ATHENA]: ({ auth, mKey, activeSiteId }) => (
    auth?.accessToken && mKey && activeSiteId ? `/review/${mKey}` : '/home'
  ),
};

export default function FhxSsoLogin() {
  const {
    auth,
    setAuth,
    setPointClickCareAuth,
    setPersist,
  } = useAuth();
  const { activeSiteId, setActiveSiteId, updateSiteSettings } = useSiteSettings();
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code');
  const state = searchParams.get('state');
  const {
    toastSetErrorNotification,
  } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [mKey, setMKey] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const verifyCode = async () => {
    if (!code || !state || !AUTH_HANDLERS[state]) {
      toastSetErrorNotification('Invalid authentication parameters');
      navigate('/login');
      return;
    }

    setLoading(true);
    try {
      const response = await axiosIntegrationInstance.get(
        `/${state}/auth/callback?code=${code}`,
      );

      const authHandler = AUTH_HANDLERS[state];
      authHandler(response, {
        setAuth,
        setPersist,
        setPointClickCareAuth,
        setMKey,
        setActiveSiteId,
        updateSiteSettings,
      });

      setIsAuthenticated(true);
    } catch (error) {
      toastSetErrorNotification(convertAxiosErrorToUserFacingMessage(error));
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (code) {
      verifyCode();
    }
  }, [code, state]);

  useEffect(() => {
    if (!isAuthenticated || !state) {
      return;
    }

    const navigationHandler = NAVIGATION_HANDLERS[state];
    if (navigationHandler) {
      const path = navigationHandler({ auth, mKey, activeSiteId });
      navigate(path);
    } else {
      navigate('/login');
    }
  }, [auth?.accessToken, mKey, isAuthenticated, state]);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
      }}
    >
      {loading && <CircularProgress />}
    </Box>
  );
}
